import { useDispatch } from 'react-redux'
import { UseQueryResult, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getGlobalLocalisationSettings, getLocalisationSettings, updateLocalisationSettings } from 'app/auth/state/actions'
import { AsyncAction } from '@cbgms/base/redux/async-action'
import { Locales } from '@carsys/enums/locales'
import { GETLocalisationSettingsResponse, ILocalizationSettings, PUTLocalisationSettingsBody } from '@cbgms/api'
import { Time } from '@carsys/enums/time'
import { localisationSettingsCacheKey, supportedLanguagesCacheKey } from 'app/menu/components/UserSettings/cache-keys'
import { useTranslation } from 'react-i18next'

interface UseUserLocalisationSettingsResult {
  localisationSettings: UseQueryResult<ILocalizationSettings>
}

export const useUserLocalisationSettings = (): UseUserLocalisationSettingsResult => {
  const dispatch = useDispatch()

  const localisationSettings = useQuery(
    localisationSettingsCacheKey,
    async () => {
      const { Data } = await dispatch(AsyncAction<GETLocalisationSettingsResponse>(getLocalisationSettings('user')))
      return Data
    },
    {
      staleTime: Time.Minute * 10
    }
  )

  return {
    localisationSettings
  }
}

interface updateUserLocalisationSettings {
  data: PUTLocalisationSettingsBody
  level?: string
}
export const UseUpdateUserLocalisationSettings = () => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  return useMutation(
    async (settings: updateUserLocalisationSettings) => {
      const { data, level } = settings
      return dispatch(updateLocalisationSettings(data, level))
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(localisationSettingsCacheKey)
      }
    }
  )
}

interface UseSupportedLanguageResult {
  languages: UseQueryResult<null, unknown>
}

export const useSupportedLanguages = (supportedLanguages: Locales[]): UseSupportedLanguageResult => {
  const { i18n } = useTranslation()

  const languages = useQuery(
    [...supportedLanguagesCacheKey, supportedLanguages],
    async () => {
      await i18n.loadLanguages(supportedLanguages)
      return null
    },
    {
      staleTime: Time.Minute * 10
    }
  )

  return {
    languages
  }
}

interface UseGlobalSettingsResult {
  globalSettings: UseQueryResult<{ Locale: Locales; Timezone: string; TimeNotation: string; DateNotation: string }>
}

export const useGlobalSettings = (): UseGlobalSettingsResult => {
  const dispatch = useDispatch()

  const globalSettings = useQuery(
    localisationSettingsCacheKey,
    async () => {
      const { Data } = await dispatch(getGlobalLocalisationSettings())
      return Data
    },
    {
      staleTime: Time.Minute * 10
    }
  )

  return {
    globalSettings
  }
}
